import React, { useState, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { StoreState } from 'store'
import { Button, Stack, Tooltip } from '@chakra-ui/react'
import { RentalApplication } from '@homevest/types/rental-applications'
import { CAPABILITY_TYPES } from '@homevest/utils/capabilities'
import { formatMoney } from '@homevest/utils/numbers'
import {
  RENTAL_APPLICATION_STATUSES,
  UNDERWRITING_METHODOLOGIES
} from '@homevest/utils/rental-applications'

import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import {
  useActiveRentalApplicationDecisionByRentalApplicationIdQuery,
  useRentalApplicationInitialUnderwritingDataQuery
} from 'graphql/generated'
import { hasCapability } from 'lib/admin-perms'
import { PreApprovalModal } from './PreApprovalModal'
import { ApprovalModal } from './ApprovalModal'
import { RentalAppQualificationResponse } from './useRentalAppQualification'

const DecisionPanel = ({
  rentalApplication,
  qualificationResponse
}: {
  rentalApplication: RentalApplication
  qualificationResponse: RentalAppQualificationResponse
}) => {
  const [isApproveModalOpen, setIsApprovedModalOpen] = useState(false)
  const [isPreApprovedModalOpen, setIsPreApprovedModalOpen] = useState(false)
  const [{ data, error }, refetchDecision] =
    useActiveRentalApplicationDecisionByRentalApplicationIdQuery({
      variables: { rentalApplicationId: rentalApplication.id }
    })
  const [{ data: initialUnderwritingData }, refetchInitialUnderwritingData] =
    useRentalApplicationInitialUnderwritingDataQuery({
      variables: { rentalApplicationId: rentalApplication.id }
    })
  const admin = useSelector((store: StoreState) => store.admin)
  const canUnderwrite = hasCapability(admin, CAPABILITY_TYPES.APPLICATION_UNDERWRITER)

  let decisionBadge: ReactNode
  let decisionBy: string | null = null
  let decisionDetails = error?.message ?? ''
  let isPreapproved = initialUnderwritingData?.rental_applications_by_pk?.preapproved_at
    ? true
    : false
  let isUndecided = true

  if (data) {
    const { rental_application_decisions } = data
    if (rental_application_decisions.length === 1) {
      const {
        admin: { first_name, last_name },
        decided_at,
        deposit_rent_multiple,
        is_approved,
        weighted_average_credit_score_at_decision,
        usable_income_at_decision
      } = rental_application_decisions[0]

      const wacs = weighted_average_credit_score_at_decision?.toFixed(0)
      const usableIncome = usable_income_at_decision
        ? formatMoney(usable_income_at_decision, 0, '$')
        : '$N/A'

      if (is_approved) {
        const decision = `Approved (${deposit_rent_multiple}x deposit)`
        decisionBadge = mapContentToBadge(decision, {
          colorOverride: 'Green',
          size: 'base',
          dot: true,
          formatFn: (x) => x
        })
        decisionBy = `by ${first_name} ${last_name} on ${new Date(decided_at).toLocaleDateString()}`
        decisionDetails = `WACS: ${wacs} | Usable Income: ${usableIncome}`
        isUndecided = false
      } else {
        // If not approved, treat as undecided
        decisionBadge = mapContentToBadge('Undecided', { size: 'base', dot: true })
        isUndecided = true
      }
    } else if (rental_application_decisions.length === 0) {
      decisionBadge = mapContentToBadge('Undecided', { size: 'base', dot: true })
      isUndecided = true
    } else {
      decisionBadge = mapContentToBadge('Unknown', {
        colorOverride: 'Red',
        size: 'base',
        dot: true
      })
      decisionDetails = 'More than one active decision - contact Engineering'
      isUndecided = true
    }
  }

  const isCompleteOrCanceled = [
    RENTAL_APPLICATION_STATUSES.COMPLETE,
    RENTAL_APPLICATION_STATUSES.CANCELED
  ].includes(rentalApplication.status as any)

  const isSaferent =
    rentalApplication.underwriting_methodology === UNDERWRITING_METHODOLOGIES.SAFERENT

  return (
    <>
      {!isCompleteOrCanceled && (
        <>
          {isSaferent && !isPreapproved ? (
            <div className='flex flex-row items-center justify-between gap-3'>
              <Tooltip
                isDisabled={canUnderwrite}
                label='You must have the "Application Underwriter" capability to preapprove an application'
              >
                <Button
                  colorScheme='teal'
                  variant='outline'
                  size='sm'
                  onClick={() => setIsPreApprovedModalOpen(true)}
                  isDisabled={!canUnderwrite}
                >
                  Pre-Approve
                </Button>
              </Tooltip>
            </div>
          ) : (
            <div className='flex flex-row items-center justify-between gap-3'>
              <Tooltip
                isDisabled={canUnderwrite}
                label='You must have the "Application Underwriter" capability to approve an application'
              >
                <Button
                  colorScheme='teal'
                  variant='outline'
                  size='sm'
                  onClick={() => setIsApprovedModalOpen(true)}
                  isDisabled={!canUnderwrite || !isUndecided}
                >
                  Approve
                </Button>
              </Tooltip>
              <Stack>
                <div className='w-fit'>{decisionBadge}</div>
                {decisionBy && <div className='mt-1 text-sm text-neutral-600'>{decisionBy}</div>}
                {decisionDetails && (
                  <div className='mt-1 text-sm text-neutral-600'>{decisionDetails}</div>
                )}
              </Stack>
            </div>
          )}
        </>
      )}
      <PreApprovalModal
        initialUnderwritingData={initialUnderwritingData?.rental_applications_by_pk ?? undefined}
        isOpen={isPreApprovedModalOpen}
        onClose={() => {
          refetchInitialUnderwritingData({ requestPolicy: 'network-only' })
          setIsPreApprovedModalOpen(false)
        }}
      />
      <ApprovalModal
        rentalApplication={rentalApplication}
        qualificationResponse={qualificationResponse}
        isOpen={isApproveModalOpen}
        onClose={() => {
          refetchDecision({ requestPolicy: 'network-only' })
          setIsApprovedModalOpen(false)
        }}
      />
    </>
  )
}

export default DecisionPanel
